import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { MenuItem } from '../../../core/models/menu-item.model';
import { ControleSessaoService } from '../../../core/services/controle-sessao.service';
import { ResolverModuloSidebarService } from '../../../core/services/resolver-modulo-sidebar.service';
import { filter } from 'rxjs';
import { LucideAngularModule } from 'lucide-angular';

type ModuloItem = { id: string; titulo: string; modulo: any }; //aqui temos as informacoes de cada modulo

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, LucideAngularModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Input() isExpanded: boolean = true;
  @Output() titleChange = new EventEmitter<string>();
  @Output() toggleSidebar = new EventEmitter<void>(); //controle de abrir e fechar

  menuItems: MenuItem[] = [];
  modulosDisponiveis: ModuloItem[] = [];
  mapaModulos: Record<string, ModuloItem> = {};

  itensFixados: MenuItem[] = [];
  fixadosAbertos: { [id: string]: boolean } = {};

  itemAtivoIndex = 0; //qual item ta selecionado
  indiceSubmenuAtivo = -1; //no inicio nenhum item ta selecioando
  tituloSubmenuAtivo = ''; //guarda o título do submenu atualmente selecionado
  submenusAbertos: { [key: number]: boolean } = {}; //controla quais submenus do menu principal estão abertos

  isDarkMode: boolean = false;

  private routerSubscription: any;
  private fixadosListener = this.atualizarFixados.bind(this);
  private loginListener = this.onLoginSucesso.bind(this);
  private storageListener = (e: StorageEvent) => {
    if (!e.key) return;

    if (e.key === 'tema') this.syncTema();

    if (
      e.key.startsWith('modulos_fixados::') ||
      e.key === 'modulos_disponiveis'
    ) {
      this.carregarModulosDisponiveis();
      this.atualizarFixados();
    }

    if (e.key === 'menu_sidebar') {
      this.carregarMenuContextual();
      this.atualizarFixados();
      this.definirAtivoPelaRotaAtual();
      this.emitirTituloAtual();
    }
  };

  constructor(
    private router: Router,
    private controleSessao: ControleSessaoService,
    private resolverModuloSidebar: ResolverModuloSidebarService,
  ) {}

  ngOnInit(): void {
    this.submenusAbertos = {};
    this.syncTema();

    this.carregarMenuContextual();
    this.carregarModulosDisponiveis();
    this.atualizarFixados();

    window.addEventListener(
      'modulos-fixados-atualizados',
      this.fixadosListener,
    );
    window.addEventListener('usuario-logado', this.loginListener);
    window.addEventListener('storage', this.storageListener);

    this.definirAtivoPelaRotaAtual();
    this.emitirTituloAtual();

    // Listener para mudanças de rota
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        console.log('[SIDEBAR] Navegação detectada:', this.router.url);
        this.definirAtivoPelaRotaAtual();
      });
  }

  ngOnDestroy(): void {
    window.removeEventListener(
      'modulos-fixados-atualizados',
      this.fixadosListener,
    );
    window.removeEventListener('usuario-logado', this.loginListener);
    window.removeEventListener('storage', this.storageListener);

    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  // ================= MENU CONTEXTUAL =================

  private carregarMenuContextual(): void {
    const menu = localStorage.getItem('menu_sidebar');
    let menuItems = menu ? (JSON.parse(menu) as MenuItem[]) : [];

    // Filtrar itens de "criar" que agora são modais
    menuItems = this.filtrarItensDeMenu(menuItems);

    this.menuItems = menuItems;

    const savedIndex = localStorage.getItem('itemAtivoIndex');
    if (savedIndex !== null) this.itemAtivoIndex = parseInt(savedIndex, 10);
  }

  private filtrarItensDeMenu(items: MenuItem[]): MenuItem[] {
    return items
      .map((item) => {
        // Filtrar subitens
        if (item.subItems && item.subItems.length > 0) {
          item.subItems = item.subItems.filter((subItem) => {
            // Remover itens que terminam com "/criar"
            return !subItem.path?.endsWith('/criar');
          });
        }
        return item;
      })
      .filter((item) => {
        // Remover item pai se não tiver mais subitens (opcional)
        if (item.subItems && item.subItems.length === 0 && !item.path) {
          return false;
        }
        // Remover item se a própria URL termina com "/criar"
        return !item.path?.endsWith('/criar');
      });
  }

  // ================= CATÁLOGO DE MÓDULOS =================

  private carregarModulosDisponiveis(): void {
    const doStorage = this.lerModulosDoStorage();
    if (doStorage.length) {
      this.modulosDisponiveis = doStorage;
    } else {
      this.modulosDisponiveis = this.montarModulosAPartirDoUsuario();
      try {
        localStorage.setItem(
          'modulos_disponiveis',
          JSON.stringify(this.modulosDisponiveis),
        );
      } catch {}
    }
    this.indexarModulos();
  }

  private lerModulosDoStorage(): ModuloItem[] {
    try {
      const raw = localStorage.getItem('modulos_disponiveis');
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  }

  private montarModulosAPartirDoUsuario(): ModuloItem[] {
    const usuario: any = this.controleSessao.getUsuarioSessao();
    const sistemas = usuario?.sistemas || [];
    const lista: ModuloItem[] = [];

    sistemas.forEach((sis: any) => {
      (sis?.modulos || []).forEach((mod: any) => {
        const id = this.obterIdModulo(mod);
        const titulo = mod?.nome || 'Módulo';
        lista.push({ id, titulo, modulo: mod });
      });
    });

    return lista;
  }

  private indexarModulos(): void {
    const idx: Record<string, ModuloItem> = {};
    this.modulosDisponiveis.forEach((c) => (idx[c.id] = c));
    this.mapaModulos = idx;
  }

  // ================= UTIL =================

  private syncTema(): void {
    this.isDarkMode = localStorage.getItem('tema') === 'escuro';
  }

  isInModulosPage(): boolean {
    return this.router.url.startsWith('/modulos');
  }
  //controle para abrir e fechar a sidebar
  onToggleSidebarClick(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.toggleSidebar.emit();
  }

  // ================= NAVEGAÇÃO (NOVA LÓGICA) =================

  private navegarSeguro(path?: string): void {
    const raw = (path || '').trim();
    if (!raw) return;

    // garante absoluto
    const destino = raw.startsWith('/') ? raw : `/${raw}`;
    this.router.navigateByUrl(destino);
  }

  // ================= ESTADO ATIVO / TÍTULO =================

  setActive(index: number): void {
    this.itemAtivoIndex = index;
    this.indiceSubmenuAtivo = -1;
    this.tituloSubmenuAtivo = '';
    localStorage.setItem('itemAtivoIndex', index.toString());

    const item = this.menuItems[index];

    if (item?.moduloCompleto) {
      localStorage.setItem(
        'modulo_selecionado',
        JSON.stringify(item.moduloCompleto),
      );
    }

    const temSub = !!item?.subItems?.length;

    if (temSub) {
      this.toggleSubMenu(index);
      this.emitirTituloAtual();
      return;
    }

    this.emitirTituloAtual();
  }

  definirAtivoPelaRotaAtual(): void {
    const rotaAtual = this.router.url.split('?')[0].split('#')[0];

    console.log('[SIDEBAR] Definindo ativo pela rota:', rotaAtual);
    console.log('[SIDEBAR] Total de menu items:', this.menuItems.length);
    console.log(
      '[SIDEBAR] Menu items:',
      this.menuItems.map((m) => ({
        titulo: m.titulo,
        path: m.path,
        subs:
          m.subItems?.map((s) => ({ titulo: s.titulo, path: s.path })) || [],
      })),
    );

    // 1. Primeiro tenta match exato em subitens
    for (let i = 0; i < this.menuItems.length; i++) {
      const item = this.menuItems[i];
      const subs = item.subItems || [];
      for (let j = 0; j < subs.length; j++) {
        const sub = subs[j];
        const subPath = (sub.path || '').split('?')[0].split('#')[0];

        console.log(
          `[SIDEBAR] Comparando "${rotaAtual}" com subitem "${subPath}"`,
        );

        if (subPath === rotaAtual) {
          console.log(
            '[SIDEBAR] ✅ Match exato em subitem:',
            item.titulo,
            '/',
            sub.titulo,
          );
          this.itemAtivoIndex = i;
          this.indiceSubmenuAtivo = j;
          this.submenusAbertos[i] = true;
          this.tituloSubmenuAtivo = sub.titulo;
          this.titleChange.emit(`${item.titulo} / ${sub.titulo}`);
          return;
        }
      }
    }

    // 2. Se não encontrou match exato, procura pelo match de prefixo mais longo (mais específico)
    let melhorMatch = { index: -1, pathLength: 0 };

    for (let i = 0; i < this.menuItems.length; i++) {
      const item = this.menuItems[i];
      const itemPath = (item.path || '').split('?')[0].split('#')[0];

      // Verifica se a rota atual começa com o path do item
      if (itemPath && rotaAtual.startsWith(itemPath)) {
        // Garante que /empresas não pegue /empresa por engano
        // A rota deve ser exatamente o path OU começar com path/
        const isExactOrStartsWithSlash =
          rotaAtual === itemPath || rotaAtual.startsWith(itemPath + '/');

        if (
          isExactOrStartsWithSlash &&
          itemPath.length > melhorMatch.pathLength
        ) {
          melhorMatch = { index: i, pathLength: itemPath.length };
        }
      }
    }

    if (melhorMatch.index >= 0) {
      const item = this.menuItems[melhorMatch.index];
      console.log(
        '[SIDEBAR] Match por prefixo no item principal:',
        item.titulo,
        '(',
        item.path,
        ')',
      );
      this.itemAtivoIndex = melhorMatch.index;
      this.indiceSubmenuAtivo = -1;
      this.tituloSubmenuAtivo = '';

      // Se tem subitens, abre o submenu
      if (item.subItems && item.subItems.length > 0) {
        this.submenusAbertos[melhorMatch.index] = true;
      }

      this.titleChange.emit(item.titulo);
      return;
    }

    console.log('[SIDEBAR] Nenhum match encontrado para:', rotaAtual);
  }

  selecionarSubOpcao(index: number, subIndex: number): void {
    this.itemAtivoIndex = index;
    this.indiceSubmenuAtivo = subIndex;
    localStorage.setItem('itemAtivoIndex', index.toString());

    const subItem = this.menuItems[index]?.subItems?.[subIndex];
    const principal = this.menuItems[index]?.titulo || '';
    if (!subItem) return;

    console.log('[SIDEBAR CLICK SUB]', {
      modulo: principal,
      sub: subItem?.titulo,
      path: subItem?.path,
      rotaAtual: this.router.url,
    });

    this.tituloSubmenuAtivo = subItem.titulo;
    this.titleChange.emit(`${principal} / ${subItem.titulo}`);

    const moduloSelecionado = this.menuItems[index]?.moduloCompleto;
    if (moduloSelecionado) {
      localStorage.setItem(
        'modulo_selecionado',
        JSON.stringify(moduloSelecionado),
      );
    }

    this.navegarSeguro(subItem.path);
  }

  selecionarSubOpcaoFixado(fx: MenuItem, subIndex: number): void {
    const indexNormal = this.menuItems.findIndex(
      (m) =>
        this.obterIdModulo(m.moduloCompleto) ===
        this.obterIdModulo(fx.moduloCompleto),
    );

    // se não existe no menu normal, navega direto pelo fixado
    if (indexNormal < 0) {
      const sub = fx.subItems?.[subIndex];
      if (!sub) return;

      this.indiceSubmenuAtivo = subIndex;
      this.tituloSubmenuAtivo = sub.titulo;
      this.titleChange.emit(`${fx.titulo} / ${sub.titulo}`);

      this.navegarSeguro(sub.path);
      return;
    }

    this.selecionarSubOpcao(indexNormal, subIndex);
  }

  toggleSubMenu(index: number): void {
    Object.keys(this.submenusAbertos).forEach((key) => {
      const i = parseInt(key, 10);
      if (i !== index) this.submenusAbertos[i] = false;
    });
    this.submenusAbertos[index] = !this.submenusAbertos[index];
  }

  emitirTituloAtual(): void {
    const item = this.menuItems[this.itemAtivoIndex];
    if (!item) return;

    if (this.indiceSubmenuAtivo >= 0) {
      const sub = item.subItems?.[this.indiceSubmenuAtivo];
      const subTitulo = sub?.titulo || this.tituloSubmenuAtivo;
      if (subTitulo) {
        this.titleChange.emit(`${item.titulo} / ${subTitulo}`);
        return;
      }
    }

    this.titleChange.emit(item.titulo);
  }

  private onLoginSucesso(): void {
    console.log('[SIDEBAR] Login detectado, recarregando módulos fixados...');

    // Dar um pequeno delay para garantir que o usuário foi salvo no sessionStorage
    setTimeout(() => {
      const idUsuario = this.obterIdUsuario();
      const chave = this.chaveFixos();
      console.log('[SIDEBAR FIXADOS] Após login - verificando:', {
        idUsuario,
        chave,
      });

      this.carregarMenuContextual();
      this.carregarModulosDisponiveis();
      this.atualizarFixados();
      this.definirAtivoPelaRotaAtual();
      this.emitirTituloAtual();
    }, 100);
  }

  resolverIconeMenuItem(item: any): string {
    const modulo = item?.moduloCompleto ?? item?.modulo ?? item;
    return this.resolverModuloSidebar.resolver(modulo).iconName;
  }

  logout(): void {
    const temaSalvo = localStorage.getItem('tema');
    const chaveFixos = this.chaveFixos();
    const fixadosAntesLogout = localStorage.getItem(chaveFixos);

    console.log('[SIDEBAR FIXADOS] Logout - preservando:', {
      chaveFixos,
      fixados: fixadosAntesLogout,
    });

    this.controleSessao.logoutSessaoUsuario();

    // Não usar clear() - remover apenas chaves específicas
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !key.startsWith('modulos_fixados::') && key !== 'tema') {
        keysToRemove.push(key);
      }
    }
    console.log('[SIDEBAR FIXADOS] Removendo chaves:', keysToRemove);
    keysToRemove.forEach((key) => localStorage.removeItem(key));

    // Verificar se fixados ainda estão lá
    const fixadosDepoisLogout = localStorage.getItem(chaveFixos);
    console.log('[SIDEBAR FIXADOS] Após logout:', {
      fixados: fixadosDepoisLogout,
    });

    // Restaurar tema
    if (temaSalvo) localStorage.setItem('tema', temaSalvo);

    this.router.navigate(['/acesso/login']);
  }

  private obterIdUsuario(): string {
    const u: any = this.controleSessao.getUsuarioSessao();
    const cand =
      u?.id ??
      u?.usuario?.id ??
      u?.usuarioId ??
      u?.usuario ??
      u?.email ??
      u?.login ??
      u?.nomeUsuario ??
      u?.cpf ??
      'anon';
    const resultado = String(cand);
    console.log(
      '[SIDEBAR FIXADOS] ID do usuário:',
      resultado,
      'Usuario completo:',
      u,
    );
    return resultado;
  }

  private chaveFixos(): string {
    return `modulos_fixados::${this.obterIdUsuario()}`;
  }

  private lerIdsFixados(): Set<string> {
    try {
      const arr = JSON.parse(localStorage.getItem(this.chaveFixos()) || '[]');
      return new Set<string>(Array.isArray(arr) ? arr : []);
    } catch {
      return new Set<string>();
    }
  }

  private salvarIdsFixados(ids: Set<string>): void {
    localStorage.setItem(this.chaveFixos(), JSON.stringify(Array.from(ids)));
  }

  obterIdModulo(mod: any): string {
    return String(
      mod?.id ??
        `${mod?.nome}::${(mod?.caminho_exibicao || '').replace(/^\/|\/$/g, '')}`,
    );
  }

  atualizarFixados(): void {
    const ids = this.lerIdsFixados();
    const lista: MenuItem[] = [];

    this.menuItems.forEach((item) => {
      const modulo = item?.moduloCompleto;
      if (!modulo) return;

      const id = this.obterIdModulo(modulo);
      if (ids.has(id)) lista.push(item);
    });

    const novoEstado: { [id: string]: boolean } = {};
    lista.forEach((fx) => {
      const id = this.obterIdModulo(fx.moduloCompleto);
      novoEstado[id] = this.fixadosAbertos[id] ?? false;
    });

    this.fixadosAbertos = novoEstado;
    this.itensFixados = lista;
  }

  alternarFixacaoSidebar(modulo: any): void {
    const ids = this.lerIdsFixados();
    const id = this.obterIdModulo(modulo);
    const chave = this.chaveFixos();

    console.log('[SIDEBAR FIXADOS] Alterando fixação:', {
      id,
      chave,
      fixadosAtuais: Array.from(ids),
    });

    if (ids.has(id)) ids.delete(id);
    else ids.add(id);

    this.salvarIdsFixados(ids);
    console.log('[SIDEBAR FIXADOS] Salvos em localStorage:', {
      chave,
      ids: Array.from(ids),
    });

    this.atualizarFixados();

    window.dispatchEvent(new CustomEvent('modulos-fixados-atualizados'));
  }

  alternarSubmenuFixado(modulo: any): void {
    const id = this.obterIdModulo(modulo);
    this.fixadosAbertos[id] = !this.fixadosAbertos[id];
  }

  navegar(path: string): void {
    this.navegarSeguro(path);
  }
}
